import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { retryQuery, retryQueryDelay } from '@/api/query-retry';

/**
 * The query cache, and the retry policies that matter.
 *
 * **A refusal is never retried, with one bounded exception.** An `ApiError` is the server saying no
 * to a request it understood: a 403, a 404, a 409, a 422. Retrying one changes nothing and, on the
 * routes that write an audit row for a refused read, turns one operator mistake into several rows in
 * a table that admits no DELETE. Only a `TransportError` is retried freely, and only twice,
 * because that is the failure a retry can actually fix.
 *
 * **The exception is a SHORT rate limit, retried once, after exactly the wait it named.** A 429 is
 * raised before any handler runs, so nothing was read, written or audited, and a refusal of a few
 * seconds — what the outer proxy answers a burst with — is one a screen should get over by itself
 * rather than sit showing an error until somebody reloads. A longer one (the application's own
 * address tier holds for a minute) is shown, not waited out behind a spinner, and the screen's own
 * refocus or poll asks again. Retrying any sooner than `Retry-After` would only be refused again.
 *
 * **A mutation is never retried automatically, even a transport failure.** The idempotency key
 * makes a retry SAFE, not free: a second attempt that succeeds after the operator has already been
 * shown a failure is a confusing outcome at a desk, and the honest thing is to let them press the
 * button again — which reuses the same key, because the key belongs to the submission rather than
 * to the attempt.
 *
 * **`staleTime` is short and `refetchOnWindowFocus` is on.** A terminal at a gate is looked at
 * again after somebody walks away from it, and the first thing the operator needs is a figure that
 * is current rather than one from before the last shift.
 *
 * The client is created inside state rather than at module scope, so a test rendering several trees
 * in one process does not share one cache between them.
 */

export function QueryProvider({ children }: Readonly<{ children: ReactNode }>): ReactNode {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: true,
            retry: retryQuery,
            retryDelay: retryQueryDelay,
          },
          mutations: { retry: false },
        },
      }),
  );

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
